import { Layout } from "@/components/Layout";
import { IMAGES } from "@/assets/images";
import { motion } from "framer-motion";
import { Target, Eye, Lightbulb, Users, Award, TrendingUp } from "lucide-react";

export default function About() {
  const values = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Mission",
      description: "To empower businesses with cutting-edge AI-led IT solutions that drive digital transformation and sustainable growth in the Canadian market."
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Vision",
      description: "To be Canada's most trusted partner in AI-driven technology innovation, setting new standards for excellence in IT services and business solutions."
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Innovation",
      description: "Leveraging 60+ years of combined expertise to deliver forward-thinking solutions that anticipate and exceed client expectations."
    }
  ];

  const strengths = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Expert Leadership",
      stat: "60+ Years",
      description: "Combined experience across sales, technology, marine engineering, and IT delivery"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Proven Track Record",
      stat: "100%",
      description: "Client satisfaction through personalized, AI-powered solutions"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Growth Focus",
      stat: "2026+",
      description: "Positioned at the forefront of AI and cloud transformation"
    }
  ];

  return (
    <Layout>
      <div className="min-h-screen">
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={IMAGES.BUSINESS_MEETING_6} 
              alt="Professional business environment" 
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                About ALTCloud Canada
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Pioneering AI-led IT services with a foundation of trust, expertise, and innovation across Canada
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-card">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.5 }}
                  className="bg-background rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-6 text-primary">
                    {value.icon}
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">{value.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={IMAGES.AI_BACKGROUND_5} 
              alt="AI technology background" 
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background/80" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-4xl font-bold mb-12 text-center">Our Story</h2>
              <div className="space-y-6 text-lg leading-relaxed">
                <p className="text-muted-foreground">
                  ALTCloud AI Canada helps organizations harness the power of AI and cloud to drive measurable business outcomes. We combine deep global expertise with a strong understanding of Canada's evolving enterprise and public-sector landscape to deliver practical, scalable, and future-ready solutions.
                </p>
                <p className="text-muted-foreground">
                  With over 60 years of combined leadership experience across digital transformation, AI/ML initiatives, cloud modernization, and global IT delivery, our team brings both strategic vision and operational rigor. We specialize in AI-led modernization, intelligent automation, data platforms, managed services, and cloud-native architectures — always with a disciplined focus on cost optimization and return on investment.
                </p>
                <p className="text-muted-foreground">
                  Headquartered in Ontario and serving clients across Canada, ALTCloud AI Canada is committed to expanding intelligent, sustainable innovation across industries — turning technology investments into competitive advantage.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-card">
          <div className="container mx-auto px-4">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-4xl font-bold mb-16 text-center"
            >
              Why Choose ALTCloud Canada
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {strengths.map((strength, index) => (
                <motion.div
                  key={strength.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.5 }}
                  className="bg-background rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] border border-border"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-6 text-primary-foreground">
                    {strength.icon}
                  </div>
                  <div className="text-3xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    {strength.stat}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{strength.title}</h3>
                  <p className="text-muted-foreground">{strength.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
              <p className="text-xl text-muted-foreground mb-8">
                Partner with ALTCloud Canada and experience the power of AI-led IT services backed by decades of expertise.
              </p>
              <a 
                href="mailto:reach@altcloudai.com"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-primary to-accent text-primary-foreground font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.97]"
              >
                Get in Touch
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}